/**
 * Edge function error helpers.
 *
 * Centralizes detection + UX for the `no_configured_provider` signal that
 * `_shared/modelChain.ts` raises when the caller has neither a BYOK key NOR a
 * platform key for any provider in the model priority chain. Multiple edge
 * functions surface this differently:
 *
 *   - positioning-suggest: HTTP 412 + `{ code: "no_configured_provider" }`
 *   - llm-proxy/chat:      HTTP 503 + `{ error: "no_configured_provider" }`
 *   - llm-proxy/analyze-*: HTTP 502 + `{ error: "provider_error", detail: "no_configured_provider: ..." }`
 *   - simulator/*:         HTTP 502 + `{ error: "no_provider_key", ... }`
 *   - simulator/transcribe: HTTP 402 + `{ error: "no_transcription_key" }`
 *
 * `parseEdgeFnError` normalizes all of the above into `NoConfiguredProviderError`
 * which `userMessages.toastError` then renders as a toast with a primary CTA
 * pointing at /onboarding?step=conexoes (incomplete users) or /configuracoes
 * (completed users).
 *
 * Spec: Front 2 of the BYOK-mandatory rollout (Sprint 1.1).
 */

import { toast } from "sonner";
import { announceError } from "@/lib/a11yLiveRegion";

// ── Error class ─────────────────────────────────────────────────────────────

export class NoConfiguredProviderError extends Error {
  readonly isNoConfiguredProvider = true as const;
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, opts?: { status?: number; code?: string }) {
    super(message);
    this.name = "NoConfiguredProviderError";
    this.status = opts?.status;
    this.code = opts?.code;
  }
}

export function isNoConfiguredProviderError(err: unknown): err is NoConfiguredProviderError {
  if (!err || typeof err !== "object") return false;
  const e = err as { isNoConfiguredProvider?: unknown; name?: unknown };
  return e.isNoConfiguredProvider === true || e.name === "NoConfiguredProviderError";
}

// ── Body shape detection ────────────────────────────────────────────────────

interface MaybeErrorBody {
  error?: unknown;
  code?: unknown;
  detail?: unknown;
  message?: unknown;
}

const NO_PROVIDER_TOKENS = [
  "no_configured_provider",
  "no_provider_key",
  "no_transcription_key",
];

function bodySignalsNoProvider(body: MaybeErrorBody | null | undefined): boolean {
  if (!body) return false;
  const fields: unknown[] = [body.error, body.code, body.detail, body.message];
  return fields.some((f) => {
    if (typeof f !== "string") return false;
    return NO_PROVIDER_TOKENS.some((tok) => f.includes(tok));
  });
}

/**
 * Returns true when the (status, body) pair indicates the caller has no key
 * configured for any provider in the model chain. We accept a broad set of
 * statuses (412/502/503/402) because edge functions are inconsistent today —
 * the body signal is the source of truth; the status is just a hint.
 */
export function isNoConfiguredProviderResponse(
  status: number,
  body: MaybeErrorBody | null | undefined,
): boolean {
  if (bodySignalsNoProvider(body)) return true;
  // Defensive fallback: positioning-suggest historically only signaled via 412.
  if (status === 412) return true;
  return false;
}

/**
 * Parse an errored Response and throw an appropriate Error. Always throws —
 * never returns. Use after `if (!resp.ok)`.
 *
 * If the response signals no configured provider, throws
 * `NoConfiguredProviderError`. Otherwise throws a generic `Error` whose
 * message is the best human-readable string we can extract from the body.
 *
 * @param fallback Message to use if the body has nothing useful.
 */
export async function throwEdgeFnError(resp: Response, fallback: string): Promise<never> {
  let body: MaybeErrorBody | null = null;
  try {
    body = (await resp.json()) as MaybeErrorBody;
  } catch {
    body = null;
  }

  if (isNoConfiguredProviderResponse(resp.status, body)) {
    const code =
      typeof body?.code === "string"
        ? body.code
        : typeof body?.error === "string"
          ? body.error
          : "no_configured_provider";
    const detail =
      typeof body?.detail === "string"
        ? body.detail
        : typeof body?.message === "string"
          ? body.message
          : typeof body?.error === "string"
            ? body.error
            : "no_configured_provider";
    throw new NoConfiguredProviderError(detail, { status: resp.status, code });
  }

  // Generic fallback — preserve status + code so downstream code can branch.
  const message =
    (typeof body?.detail === "string" && body.detail) ||
    (typeof body?.message === "string" && body.message) ||
    (typeof body?.error === "string" && body.error) ||
    fallback ||
    `HTTP ${resp.status}`;
  const err = new Error(message) as Error & { status?: number; code?: string };
  err.status = resp.status;
  if (typeof body?.error === "string") err.code = body.error;
  else if (typeof body?.code === "string") err.code = body.code;
  throw err;
}

// ── Toast UX ────────────────────────────────────────────────────────────────

/**
 * URL the "Configurar agora" CTA should navigate to.
 * - Incomplete onboarding → /onboarding?step=conexoes
 * - Completed (or unknown) → /configuracoes  (BYOK section is the default)
 *
 * We sniff the cached business context from the React Query cache via window
 * because this helper is called from non-React contexts (toast factory). If
 * the signal is unavailable we default to /configuracoes which is always safe.
 */
function resolveSetupUrl(): string {
  try {
    const rawPct = (
      globalThis as unknown as { __synkraOnboardingPct?: number }
    ).__synkraOnboardingPct;
    if (typeof rawPct === "number" && rawPct < 100) {
      return "/onboarding?step=conexoes";
    }
  } catch {
    /* ignore */
  }
  return "/configuracoes";
}

let activeNoProviderToastId: string | number | null = null;

/**
 * Renders the BYOK-setup toast with a primary CTA. Coalesces consecutive
 * triggers so users don't see a stack of identical toasts when several
 * features fire 412 at once (common during a first session).
 */
export function showNoConfiguredProviderToast(): void {
  // Coalesce: if a toast is already open, dismiss it before re-firing so the
  // duration resets and the user doesn't get a backlog.
  if (activeNoProviderToastId !== null) {
    toast.dismiss(activeNoProviderToastId);
  }

  const url = resolveSetupUrl();
  const message = "Conecte ao menos uma chave de IA pra usar este recurso";
  const description =
    "Cadastre uma chave da OpenAI em Perfil > Conexões para destravar a IA.";

  activeNoProviderToastId = toast.error(message, {
    description,
    duration: 12_000,
    closeButton: true,
    action: {
      label: "Configurar agora",
      onClick: () => {
        // Use full navigation so this works from any context (no router hook).
        window.location.assign(url);
      },
    },
    onDismiss: () => {
      activeNoProviderToastId = null;
    },
    onAutoClose: () => {
      activeNoProviderToastId = null;
    },
  });

  announceError(message);
}
